'use client';

/**
 * Workflow Initiation Dialog
 *
 * Dialog for initiating TCO workflows for selected members.
 * Supports multi-member selection and template-based workflow creation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Search,
  Loader2,
  AlertCircle,
  Users,
  PlayCircle,
  X,
  CheckCircle,
  FileText,
  Clock,
  ArrowRight,
} from 'lucide-react';
import {
  tcoWorkflowsAPI,
  WorkflowTemplate,
  TriggerType,
} from '@/services/api/tcoServiceReferrals';
import { apiClient } from '@/services/apiClient';
import { useToast } from '@/components/ui/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface WorkflowInitiationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (workflowIds: number[]) => void;
  /** Pre-selected member IDs */
  memberIds?: number[];
  /** Pre-selected template ID */
  templateId?: number;
  className?: string;
}

interface MemberResult {
  id: number;
  name: string;
  email?: string;
}

// Trigger type display labels
const TRIGGER_TYPE_OPTIONS: { value: TriggerType; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'sdoh_screening', label: 'SDOH Screening' },
  { value: 'risk_score', label: 'Risk Score' },
  { value: 'care_plan_goal', label: 'Care Plan Goal' },
  { value: 'enrollment_event', label: 'Enrollment Event' },
  { value: 'scheduled', label: 'Scheduled' },
];

const PRIORITY_OPTIONS = [
  { value: 1, label: 'Low' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'High' },
  { value: 4, label: 'Urgent' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function WorkflowInitiationDialog({
  open,
  onClose,
  onSuccess,
  memberIds: propMemberIds,
  templateId: propTemplateId,
  className = '',
}: WorkflowInitiationDialogProps) {
  const { toast } = useToast();
  const { t } = useAppTranslation();

  // Form state
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'members' | 'template' | 'confirm'>('members');

  // Member selection
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<MemberResult[]>([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [memberSearchResults, setMemberSearchResults] = useState<MemberResult[]>([]);

  // Template selection
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(propTemplateId);

  // Configuration
  const [triggerType, setTriggerType] = useState<TriggerType>('manual');
  const [priority, setPriority] = useState(2);

  // Result state
  const [result, setResult] = useState<{
    success: boolean;
    workflows_created: number;
    workflow_ids: number[];
    errors: Array<{ member_id: number; error: string }>;
  } | null>(null);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templateList = await tcoWorkflowsAPI.listTemplates({ is_active: true });
        setTemplates(templateList);
      } catch (err) {
        console.error('Failed to load templates:', err);
        toast({
          title: t('tcoReferrals.initiation.error', 'Error'),
          description: t('tcoReferrals.initiation.failedToLoadTemplates', 'Failed to load workflow templates'),
          variant: 'destructive',
        });
      } finally {
        setLoadingTemplates(false);
      }
    };
    if (open) {
      loadTemplates();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search members with debounce
  const handleMemberSearch = useCallback(async () => {
    if (!memberSearchQuery.trim() || memberSearchQuery.length < 2) return;

    setSearchingMembers(true);
    try {
      const response = await apiClient.get('/api/enrollments/search-members', {
        params: {
          search: memberSearchQuery,
          limit: 10,
        },
      });
      const results = response.data || {};
      setMemberSearchResults(
        (results.members || []).map((m: any) => ({
          id: m.id,
          name: `${m.first_name} ${m.last_name}`,
          email: m.email,
        }))
      );
    } catch (err) {
      console.error('Failed to search members:', err);
    } finally {
      setSearchingMembers(false);
    }
  }, [memberSearchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearchQuery.length >= 2) {
        handleMemberSearch();
      } else {
        setMemberSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearchQuery, handleMemberSearch]);

  // Select member
  const handleSelectMember = (member: MemberResult) => {
    if (!selectedMembers.some((m) => m.id === member.id)) {
      setSelectedMembers((prev) => [...prev, member]);
    }
    setMemberSearchResults([]);
    setMemberSearchQuery('');
  };

  // Remove member
  const handleRemoveMember = (memberId: number) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  // Get selected template
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // Validate steps
  const canProceedToTemplate = selectedMembers.length > 0;
  const canProceedToConfirm = selectedTemplateId !== undefined;
  const canSubmit = canProceedToTemplate && canProceedToConfirm;

  // Handle submit
  const handleSubmit = async () => {
    if (!canSubmit) {
      toast({
        title: t('tcoReferrals.initiation.validationError', 'Validation Error'),
        description: t('tcoReferrals.initiation.selectMembersAndTemplate', 'Please select members and a template'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const initiateResult = await tcoWorkflowsAPI.initiate({
        member_ids: selectedMembers.map((m) => m.id),
        template_id: selectedTemplateId!,
        trigger_type: triggerType,
        priority,
      });

      setResult(initiateResult);

      if (initiateResult.success && initiateResult.workflows_created > 0) {
        toast({
          title: t('tcoReferrals.initiation.workflowsInitiated', 'Workflows Initiated'),
          description: t('tcoReferrals.initiation.successfullyCreated', 'Successfully created {{count}} workflow(s).', { count: initiateResult.workflows_created }),
        });
        onSuccess?.(initiateResult.workflow_ids);
      } else if (initiateResult.errors.length > 0) {
        toast({
          title: t('tcoReferrals.initiation.partialSuccess', 'Partial Success'),
          description: t('tcoReferrals.initiation.createdWithErrors', 'Created {{count}} workflows with {{errors}} errors.', { count: initiateResult.workflows_created, errors: initiateResult.errors.length }),
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error initiating workflows:', err);
      toast({
        title: t('tcoReferrals.initiation.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.initiation.failedToInitiate', 'Failed to initiate workflows'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form and close
  const handleClose = () => {
    setStep('members');
    setSelectedMembers([]);
    setMemberSearchQuery('');
    setMemberSearchResults([]);
    setSelectedTemplateId(propTemplateId);
    setTriggerType('manual');
    setPriority(2);
    setResult(null);
    onClose();
  };

  // Step navigation
  const goToStep = (newStep: 'members' | 'template' | 'confirm') => {
    if (newStep === 'template' && !canProceedToTemplate) return;
    if (newStep === 'confirm' && !canProceedToConfirm) return;
    setStep(newStep);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`max-w-xl max-h-[90vh] overflow-y-auto ${className}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-indigo-600" />
            {t('tcoReferrals.initiation.title', 'Start Workflow')}
          </DialogTitle>
          <DialogDescription>
            {t('tcoReferrals.initiation.description', 'Initiate a care coordination workflow for selected members.')}
          </DialogDescription>
        </DialogHeader>

        {/* Show result if submitted */}
        {result ? (
          <div className="space-y-4 py-4">
            <div className={`p-4 rounded-lg border ${result.workflows_created > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {result.workflows_created > 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="font-medium">
                  {result.workflows_created > 0
                    ? t('tcoReferrals.initiation.workflowsCreated', '{{count}} Workflow(s) Created', { count: result.workflows_created })
                    : t('tcoReferrals.initiation.noWorkflowsCreated', 'No Workflows Created')}
                </span>
              </div>
              {result.workflow_ids.length > 0 && (
                <p className="text-sm text-gray-600">
                  {t('tcoReferrals.initiation.workflowIds', 'Workflow IDs')}: {result.workflow_ids.join(', ')}
                </p>
              )}
            </div>
            {result.errors.length > 0 && (
              <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
                <p className="font-medium text-amber-800 mb-2">{t('tcoReferrals.initiation.errors', 'Errors')} ({result.errors.length})</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  {result.errors.map((err, i) => (
                    <li key={i}>{t('tcoReferrals.initiation.member', 'Member')} {err.member_id}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleClose}>{t('tcoReferrals.initiation.close', 'Close')}</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 py-2">
              <button
                onClick={() => goToStep('members')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  step === 'members' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="h-4 w-4" />
                {t('tcoReferrals.initiation.steps.members', 'Members')}
              </button>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <button
                onClick={() => canProceedToTemplate && goToStep('template')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  step === 'template' ? 'bg-indigo-100 text-indigo-700' : canProceedToTemplate ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'
                }`}
                disabled={!canProceedToTemplate}
              >
                <FileText className="h-4 w-4" />
                {t('tcoReferrals.initiation.steps.template', 'Template')}
              </button>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <button
                onClick={() => canProceedToConfirm && goToStep('confirm')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  step === 'confirm' ? 'bg-indigo-100 text-indigo-700' : canProceedToConfirm ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'
                }`}
                disabled={!canProceedToConfirm}
              >
                <CheckCircle className="h-4 w-4" />
                {t('tcoReferrals.initiation.steps.confirm', 'Confirm')}
              </button>
            </div>

            {/* Step content */}
            <div className="py-4 space-y-4">
              {/* Step 1: Member Selection */}
              {step === 'members' && (
                <div className="space-y-4">
                  <div>
                    <Label>{t('tcoReferrals.initiation.searchMembers', 'Search Members')}</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder={t('tcoReferrals.initiation.searchPlaceholder', 'Search by name or email...')}
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                      {searchingMembers && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Search results */}
                  {memberSearchResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                      {memberSearchResults.map((member) => (
                        <button
                          key={member.id}
                          onClick={() => handleSelectMember(member)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-sm">{member.name}</p>
                            {member.email && <p className="text-xs text-gray-500">{member.email}</p>}
                          </div>
                          {selectedMembers.some((m) => m.id === member.id) && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Selected members */}
                  <div>
                    <Label>{t('tcoReferrals.initiation.selectedMembers', 'Selected Members')} ({selectedMembers.length})</Label>
                    {selectedMembers.length === 0 ? (
                      <p className="text-sm text-gray-500 mt-1">{t('tcoReferrals.initiation.noMembersSelected', 'No members selected. Search and select members above.')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedMembers.map((member) => (
                          <Badge key={member.id} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                            {member.name}
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Template Selection */}
              {step === 'template' && (
                <div className="space-y-4">
                  <div>
                    <Label>{t('tcoReferrals.initiation.workflowTemplate', 'Workflow Template')}</Label>
                    {loadingTemplates ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('tcoReferrals.initiation.loadingTemplates', 'Loading templates...')}
                      </div>
                    ) : (
                      <Select
                        value={selectedTemplateId?.toString()}
                        onValueChange={(v) => setSelectedTemplateId(parseInt(v, 10))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={t('tcoReferrals.initiation.selectTemplate', 'Select a template')} />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((template) => (
                            <SelectItem key={template.id} value={template.id.toString()}>
                              {template.template_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Template preview */}
                  {selectedTemplate && (
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <h4 className="font-medium text-sm">{selectedTemplate.template_name}</h4>
                      {selectedTemplate.description && (
                        <p className="text-sm text-gray-600 mt-1">{selectedTemplate.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {selectedTemplate.workflow_steps?.length || 0} {t('tcoReferrals.initiation.steps.label', 'steps')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {selectedTemplate.trigger_type}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Configuration options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('tcoReferrals.initiation.triggerType', 'Trigger Type')}</Label>
                      <Select value={triggerType} onValueChange={(v) => setTriggerType(v as TriggerType)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TRIGGER_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('tcoReferrals.initiation.priority', 'Priority')}</Label>
                      <Select value={priority.toString()} onValueChange={(v) => setPriority(parseInt(v, 10))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === 'confirm' && (
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">{t('tcoReferrals.initiation.confirm.members', 'Members')}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedMembers.map((m) => (
                          <Badge key={m.id} variant="outline">{m.name}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('tcoReferrals.initiation.confirm.template', 'Template')}</p>
                      <p className="font-medium">{selectedTemplate?.template_name}</p>
                    </div>
                    <div className="flex gap-8">
                      <div>
                        <p className="text-sm text-gray-500">{t('tcoReferrals.initiation.confirm.triggerType', 'Trigger Type')}</p>
                        <p className="font-medium">{TRIGGER_TYPE_OPTIONS.find((t) => t.value === triggerType)?.label}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('tcoReferrals.initiation.confirm.priority', 'Priority')}</p>
                        <p className="font-medium">{PRIORITY_OPTIONS.find((p) => p.value === priority)?.label}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('tcoReferrals.initiation.confirm.willCreate', 'This will create {{count}} workflow instance(s). Each member will be enrolled in the selected workflow template.', { count: selectedMembers.length })}
                  </p>
                </div>
              )}
            </div>

            {/* Footer navigation */}
            <DialogFooter className="flex justify-between">
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose}>
                  {t('tcoReferrals.initiation.cancel', 'Cancel')}
                </Button>
                {step !== 'members' && (
                  <Button
                    variant="outline"
                    onClick={() => setStep(step === 'confirm' ? 'template' : 'members')}
                  >
                    {t('tcoReferrals.initiation.back', 'Back')}
                  </Button>
                )}
              </div>
              {step === 'confirm' ? (
                <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('tcoReferrals.initiation.initiating', 'Initiating...')}
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-4 w-4 mr-2" />
                      {t('tcoReferrals.initiation.startWorkflow', 'Start Workflow')}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setStep(step === 'members' ? 'template' : 'confirm')}
                  disabled={step === 'members' ? !canProceedToTemplate : !canProceedToConfirm}
                >
                  {t('tcoReferrals.initiation.next', 'Next')}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
